import { gql } from "lib/apolloClient";
import {
  deadlineFragments,
  projectFragments,
  insightFragments,
  guidingQuestionsFragment,
  studentProjectPortfolioFragments,
  studentPortfolioProgressFragment,
  projectFieldsFragment,
  projectGroupFragment,
  studentFragments,
  supervisorFragments,
  externalCollaboratorFragment,
  projectUserFragment,
  projectTaskResponseFragment,
  projectFieldItemFragment,
  projectFieldsResolvedMinimalTreeFragment,
} from "./ProjectFragments";
import {
  attachmentFragment,
  userFragment,
  projectGroupFragments,
  remarkFragment,
  staffFragment,
  toolResponseFragment,
  studentsAlertFragment,
  userNameFragment,
} from "modules/CommonFragments";

export const getProjectGroupGuidanceStatusQuery = gql`
  query getProjectGroupGuidanceStatus($id: ID!) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        showStudentGuidance
        showSupervisorGuidance
      }
    }
  }
`;

export const getProjectGroupDeadlinesQuery = gql`
  query getProjectGroupDetails(
    $projectGroupId: ID!
    $fetchStatusWiseProjects: Boolean!
    $deadlineFilters: ProjectGroupDeadlineFilters
    $statusWiseProjectFilters: StatusWiseProjectFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        deadlines(filters: $deadlineFilters) {
          ...projectDeadline
        }
        type
        subType
      }
    }
  }
  ${deadlineFragments.deadlineFragmentWithStatusCount}
`;

export const getProjectGroupSelectedReportTemplateQuery = gql`
  query getProjectGroupSelectedReportTemplateQuery($projectGroupId: ID!) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        selectedProjectReportTemplate {
          ...attachmentItem
          presetAttachment {
            ...attachmentItem
          }
          parentProjectGroupReport {
            ...attachmentItem
          }
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;
export const getProjectGroupProjectCountQuery = gql`
  query getProjectGroupProjectCount(
    $id: ID!
    $filters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        projects(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getUserProjectGroupPortfoliosCountQuery = gql`
  query getUserProjectGroupPortfoliosCount(
    $id: ID!
    $portfolioFilters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $portfolioFilters) {
          totalCount
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const getMultiNodeProjectGroupProjectCountQuery = gql`
  query getMultiNodeProjectGroupProjectCount(
    $ids: [ID!]
    $filters: ProjectGroupProjectsFilters
  ) {
    multiNode(ids: $ids, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        projects(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupProjectsQuery = gql`
  query getProjectGroupProjects(
    $id: ID!
    $filters: ProjectGroupProjectsFilters
    $studentCourseFilters: UserCourseMapFilters
    $skipStudentDetails: Boolean!
    $skipCourseDetails: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: $filters) {
          edges {
            node {
              ...projectStudentInfo
            }
          }
        }
      }
    }
  }
  ${projectFragments.projectStudentInfoFragment}
`;

export const getStudentsBulkDownloadReportsQuery = gql`
  query getProjectGroupProjectsSubmissionsAndFields(
    $id: ID!
    $filters: ProjectGroupProjectsFilters
    $fieldFilters: PlannerFieldFilter
    $shouldFetchFields: Boolean!
    $studentCourseFilters: UserCourseMapFilters
    $skipCourseDetails: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: $filters) {
          edges {
            node {
              id
              students {
                ...studentInfo
              }
              finalProjectReports {
                id
              }
              fields(filters: $fieldFilters) @include(if: $shouldFetchFields) {
                id
                uid
                value
                pseudoFieldUid
                children
                attachments {
                  ...attachmentItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragmentV2}
  ${attachmentFragment.attachment}
`;

export const getProjectGroupPortfoliosDataQuery = gql`
  query getProjectGroupPortfoliosData(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          edges {
            node {
              id
              student {
                ...studentInfo
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
`;

export const getProjectAssessmentBulkDownloadQuery = gql`
  query getProjectAssessmentBulkDownload(
    $id: ID!
    $projectIds: [ID!]
    $academicYearIds: [ID!]
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        assessmentCsvExport(
          filters: {
            projectIds: $projectIds
            academicYearIds: $academicYearIds
          }
        )
      }
    }
  }
`;

export const getProjectAssessmentDownloadUrlQuery = gql`
  query getProjectAssessmentDownloadUrl(
    $id: ID!
    $projectGroupAssessmentToolExportUrlInputs: ProjectGroupAssessmentToolExportUrlInput!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        generateAssessmentToolExportUrl(
          projectGroupAssessmentToolExportUrlInputs: $projectGroupAssessmentToolExportUrlInputs
        ) {
          url
          isSuccess
        }
        __typename
      }
      __typename
    }
  }
`;

export const getProjectDetailsQuery = gql`
  query getProjectDetailsQuery(
    $id: ID!
    $studentProjectPortfolioFilter: StudentProjectPortfolioFilter
    $academicYearId: ID
    $completionStatusFilters: CompletionStatusFilter
  ) {
    node(id: $id, type: PROJECT) {
      id
      ... on Project {
        id
        projectComments(filters: { projectCommentType: PROJECT_TASK }) {
          totalCount
          unreadCountV2
        }
        notificationBadgeCounts {
          unreadPostCount
          unreadPostCommentCount
          unreadCommentCount
          unreadAcademicHonestyCount
          unreadAttachmentCount
          unreadPendingReviewLogCount
          unreadResolvedReviewLogCount
          unreadPendingReviewLogCommentCount
          unreadResolvedReviewLogCommentCount
          unreadAttachmentAnnotatedCount
          unreadProjectEvidenceCommentCount
          unreadProjectTasksCommentsCount
          unreadProjectPresentationCommentsCount
        }
        studentCollaborators(filters: { roles: [COLLABORATOR] }) {
          student {
            ...studentInfo
            type
          }
        }
        students {
          ...userItem
          studentProjectPortfolio(filters: $studentProjectPortfolioFilter) {
            id
            completionStatus(filters: $completionStatusFilters) {
              isComplete
              markedCompletedAt
              markedCompletedBy {
                ...userNameFields
              }
            }
          }
          alertTypes {
            ...alertTypeFields
          }
        }
        reportStatus
        status
        supervisors(filters: { approvalStatus: [APPROVED, PENDING] }) {
          ...SupervisorInfoItemV2
        }
        grades {
          id
          name
        }
        template {
          id
          name
          body
          type
          helpEnabledFields {
            id
            uid
            type
            helpText {
              id
              title
              content
              subtitle
            }
          }
        }
        fields {
          ...projectFieldItem
        }
        projectGroup {
          id
          name
          subject {
            id
            name
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectFieldItemFragment.projectFieldItem}
  ${supervisorFragments.supervisorInfoFragmentV2}
  ${projectUserFragment.basicUser}
  ${studentFragments.studentInfoFragment}
  ${userNameFragment}
`;

export const getProjectPresentationCommentsQuery = gql`
  query getProjectPresentationComments($id: ID!) {
    node(id: $id, type: PROJECT) {
      id
      ... on Project {
        id
        projectComments(filters: { projectCommentType: PROJECT_PRESENTATION }) {
          totalCount
          unreadCountV2
        }
      }
    }
  }
`;

export const getProjectStudentCollaboratorsForSplittingQuery = gql`
  query getProjectStudentCollaboratorsForSplitting($id: ID!) {
    node(id: $id, type: PROJECT) {
      id
      ... on Project {
        id
        studentCollaborators {
          id
          student {
            ...studentInfo
          }
          fields {
            ...projectFieldItem
          }
          academicHonesty {
            id
            isRead
          }
          ibPYPElementRatings {
            id
          }
          isAcademicHonestyLocked
        }
      }
    }
  }
  ${projectFieldItemFragment.projectFieldItem}
  ${studentFragments.studentInfoFragment}
`;

export const getProjectStudentTagDetailsQuery = gql`
  query getProjectStudentTagDetails(
    $id: ID!
    $studentCourseFilters: UserCourseMapFilters
    $skipCourseDetails: Boolean!
  ) {
    node(id: $id, type: PROJECT) {
      ... on Project {
        id
        students {
          ...studentInfo
        }
      }
    }
  }
  ${studentFragments.studentInfoFragmentV2}
`;

export const getProjectFinalReportQuery = gql`
  query getProjectFinalReport($id: ID!) {
    node(id: $id, type: PROJECT) {
      id
      ... on Project {
        id
        finalProjectReports {
          id
        }
      }
    }
  }
`;

export const getProjectInfoQuery = gql`
  query getProjectInfo($projectId: ID!) {
    node(id: $projectId, type: PROJECT) {
      ... on Project {
        ...projectCompletedDeadlinesItem
      }
    }
  }
  ${projectFragments.projectCompletedDeadlinesFragment}
`;

export const getProjectHeaderInfoQuery = gql`
  query getProjectHeaderInfo(
    $projectId: ID!
    $projectFieldFilters: PlannerFieldFilter
    $studentProjectPortfolioFilter: StudentProjectPortfolioFilter
    $shouldFetchPortfolioStudents: Boolean!
    $shouldFetchFields: Boolean!
    $academicYearId: ID!
    $completionStatusFilters: CompletionStatusFilter
  ) {
    node(id: $projectId, type: PROJECT) {
      ... on Project {
        ...projectInfoItem
        fields(filters: $projectFieldFilters) @include(if: $shouldFetchFields) {
          id
          uid
          value
          pseudoFieldUid
        }
        academicYear {
          id
        }
        type
        isCasExternalPortingDone
        createdBy {
          id
        }
        isDeleted
        deletedBy {
          ...userNameFields
        }
        deletedAt
        isArchived
        archivedBy {
          ...userNameFields
        }
        archivedAt
        markedCompletedBy {
          ...userNameFields
        }
        markedCompletedAt
        updatedAt
        updatedBy {
          ...userNameFields
        }
        portfolioInfoStudents: students
          @include(if: $shouldFetchPortfolioStudents) {
          id
          studentProjectPortfolio(filters: $studentProjectPortfolioFilter) {
            id
            completionStatus(filters: $completionStatusFilters) {
              isComplete
              markedCompletedAt
              markedCompletedBy {
                ...userNameFields
              }
            }
          }
        }
        markedCompletedBy {
          ...userNameFields
        }
        markedCompletedAt
        ibPYPElementRatings(
          filters: { academicCriteriaSetTypes: [PROJECT_OVERALL_PROGRESS] }
        ) {
          id
          value
          academicCriteriaSet {
            id
            criteriaType
          }
          createdByV2 {
            ...userNameFields
            profileImage
          }
          createdAt
          academicCriteriaValue {
            id
            label
            abbreviation
            color
          }
        }
      }
    }
  }
  ${projectFragments.projectDetailsHeaderInfoFragment}
  ${userNameFragment}
`;

export const getMultiNodeProjectCountsQuery = gql`
  query getMultiNodeProjectCounts($ids: [ID!], $academicYearIds: [ID!]) {
    multiNode(ids: $ids, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: { academicYearIds: $academicYearIds }) {
          totalCount
        }
        isInvited
      }
    }
  }
`;

export const getCourseAllCollaboratorsQuery = gql`
  query getCourseAllCollaborators($id: ID!) {
    node(id: $id, type: COURSE) {
      ... on Course {
        id
        allCollaborators {
          edges {
            node {
              ...staffItem
            }
          }
        }
      }
    }
  }
  ${staffFragment.basicStaffDetails}
`;

export const getGuidingQuestionsQuery = gql`
  query getGuidingQuestions(
    $projectGroupId: ID!
    $filters: GuidingQuestionFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        guidingQuestions(filters: $filters) {
          ...guidingQuestions
        }
      }
    }
  }
  ${guidingQuestionsFragment.guidingQuestions}
`;

export const getPlannerElementInsightSpecificsQuery = gql`
  query getPlannerElementInsightSpecifics(
    $projectGroupId: ID!
    $gradeFilters: PlannerElementInsightSpecificsFilter
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        plannerElementInsightSpecifics(filters: $gradeFilters) {
          ...plannerElementInsightSpecificItem
        }
      }
    }
  }
  ${insightFragments.plannerElementInsightSpecific}
`;

export const getProjectsWithEvidenceInsightsQuery = gql`
  query getProjectsWithEvidenceInsights(
    $ids: [ID!]
    $filters: PlannerFieldFilter
    $evidenceFilters: ProjectEvidenceInsightFilters
  ) {
    multiNode(ids: $ids, type: PROJECT) {
      ... on Project {
        id
        title {
          id
          value
        }
        fields(filters: $filters) {
          id
          uid
          value
        }
        evidenceInsights(filters: $evidenceFilters) {
          evidence {
            edges {
              node {
                id
              }
            }
            totalCount
          }
        }
      }
    }
  }
`;

export const getPostsQuery = gql`
  query getPostsQuery($ids: [ID!]) {
    multiNode(ids: $ids, type: POST) {
      ... on Post {
        id
        title
        attachments {
          ...attachmentItem
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const getProjectGroupSupervisorsQuery = gql`
  query getProjectGroupSupervisors(
    $projectGroupId: ID!
    $filters: ProjectGroupUserFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        allSupervisors(filters: $filters) {
          totalCount
          edges {
            node {
              ...userItem
            }
          }
        }
      }
    }
  }

  ${userFragment.basicUser}
`;

export const getOrganizationStudentsQuery = gql`
  query getOrganizationStudents(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        students(filters: $filters) {
          edges {
            node {
              ...userItem
              ... on Student {
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectUserFragment.basicUser}
`;

export const getProjectGroupStudentProjectPortfoliosCountQuery = gql`
  query getProjectGroupStudentProjectPortfoliosCount(
    $id: ID!
    $portfolioFilters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $portfolioFilters) {
          totalCount
        }
      }
    }
  }
`;

export const getProjectGroupStudentProjectAdvisersQuery = gql`
  query getProjectGroupStudentProjectAdvisers(
    $id: ID!
    $portfolioFilters: StudentProjectPortfolioFilters
    $academicYearId: ID
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $portfolioFilters) {
          edges {
            node {
              id
              staff {
                ...userItem
              }
            }
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUserWithIsArchivedV2}
`;

export const getStudentProjectPortfolioDetailsQuery = gql`
  query getStudentProjectPortfolioDetails($id: ID!) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        notificationBadgeCounts {
          learningOutcomesCommentCount
          feedbackCommentCount
          feedbackCount
          interviewCount
        }
        fields {
          ...projectFieldItem
        }
      }
    }
  }
  ${projectFieldItemFragment.projectFieldItem}
`;

export const getStudentPortfolioProjectsQuery = gql`
  query getStudentProjectPortfolioProjects(
    $id: ID!
    $projectFilters: ProjectFilter
    $filters: PlannerFieldFilter
    $academicYearId: ID
    $isServiceAsAction: Boolean!
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        projects(filters: $projectFilters) {
          totalCount
          edges {
            node {
              ...portfolioProjectInfo
              ...projectFieldsDetails
              createdBy {
                id
              }
              academicYear @include(if: $isServiceAsAction) {
                id
                startDate
                endDate
              }
              notificationBadgeCounts {
                unreadPostCount
                unreadPostCommentCount
                unreadCommentCount
                unreadAcademicHonestyCount
                unreadAttachmentCount
                unreadPendingReviewLogCount
                unreadResolvedReviewLogCount
                unreadPendingReviewLogCommentCount
                unreadResolvedReviewLogCommentCount
                unreadProjectEvidenceCommentCount
              }
            }
          }
        }
      }
    }
  }
  ${projectFragments.portfolioProjectInfoFragment}
  ${projectFieldsFragment.projectFieldsDetails}
`;

export const getStudentPortfolioSupervisorsQuery = gql`
  query getStudentPortfolioSupervisorsQuery(
    $id: ID!
    $academicYearId: ID
    $filters: ProjectFilter
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        projects(filters: $filters) {
          edges {
            node {
              id
              supervisors {
                ...userItem
              }
            }
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUserWithIsArchivedV2}
`;

export const getStudentPortfolioDetailsQuery = gql`
  query getStudentPortfolioDetails(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
    $academicYearId: ID
    $plannerElementEvidenceFilters: PlannerElementsEvidenceFilter!
    $projectCountFilter: ProjectCountFilter
    $ibPYPElementRatingsFilters: ibPYPElementRatingsFilter
    $completionStatusFilters: CompletionStatusFilter
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
        pageInfo{
          hasNextPage
          endCursor
        }
          edges {
            node {
              ...portfolioProgress
              totalEvidencesCount
              student {
                ...userItem
                alertTypes {
                ...alertTypeFields
              }
                registrationCategory
              }
              staff {
                ...supervisorInfo
              }
              plannerElementEvidences(filters: $plannerElementEvidenceFilters) {
                id
                plannerElementNode {
                  id
                  label
                  subText
                  metadata
                }
                isAchieved
                grade {
                  id
                  name
                }
              }
              projectCounts {
                projectsCount (filters: $projectCountFilter)
                experiencesCount
              }
              cascadeUpdateAt
              cascadeUpdateBy {
                ...userNameFields
              }
              createdAt
              createdBy{
                ...userNameFields
              }
              notificationBadgeCounts {
                interestsAndGoalsCommentCount
                learningOutcomesCommentCount
                feedbackCount
                feedbackCommentCount
                totalUnreadCountsOfPortfolioProjects
                newProjectCount
                interviewCount
              }
              ibPYPElementRatings (
                filters: $ibPYPElementRatingsFilters
              ) {
              id
               academicCriteriaSet{
              id
              criteriaType
}
              academicCriteriaValue{
                id
                label
                color
                abbreviation
                
}
              } 
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectUserFragment.basicUser},
  ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2},
  ${studentPortfolioProgressFragment.studentPortfolioProgress}
  ${userNameFragment}
`;

export const getProjectGroupPortfoliosIdsQuery = gql`
  query getProjectGroupPortfoliosIds(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getCASInsightQuery = gql`
  query getCASInsightQuery(
    $id: ID!
    $StudentProjectPortfolioFilters: StudentProjectPortfolioFilters
    $PlannerFieldFilter: PlannerFieldFilter
    $plannerElementEvidenceFilters: PlannerElementsEvidenceFilter!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $StudentProjectPortfolioFilters) {
          edges {
            node {
              id
              achievedPlannerElements {
                totalCount
              }
              student {
                ...studentInfo
              }
              plannerElementEvidences(
                filters: $plannerElementEvidenceFilters
              ) {
                id
                plannerElementNode {
                  id
                  displaySequence
                  label
                }
                evidence {
                  totalCount
                }
                isAchieved
                projects {
                  totalCount
                  edges{
                    node{
                      id
                      title{
                        id
                        value
                      }
                      fields  (filters: $PlannerFieldFilter){
                        uid
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment},
`;

export const getCASPlannerElementEvidencesQuery = gql`
  query getCASPlannerElementEvidences(
    $id: ID!
    $plannerElementEvidenceFilters: PlannerElementsEvidenceFilter!
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        plannerElementEvidences(filters: $plannerElementEvidenceFilters) {
          id
          plannerElementNode {
            id
            displaySequence
            label
          }
          evidence {
            totalCount
            edges {
              node {
                id
                title
                description
                shareableUrl
                attachments {
                  id
                  title
                  name
                  description
                  thumbUrl
                  url
                  type
                }
                items {
                  itemType
                  item {
                    ... on Project {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getLearningOutcomesWithEvidencesQuery = gql`
  query getLearningOutcomesWithEvidences(
    $id: ID!
    $plannerElementEvidenceFilters: PlannerElementsEvidenceFilter!
    $isServiceAsAction: Boolean!
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        plannerElementEvidences(filters: $plannerElementEvidenceFilters) {
          id
          plannerElementNode {
            id
            label
            subText
            metadata
          }
          evidence {
            totalCount
          }
          comments {
            totalCount
            unreadCountV2
          }
          isAchieved
          academicYear @include(if: $isServiceAsAction) {
            id
          }
          grade {
            id
            name
          }
          ibPYPElementRatings(
            filters: { orderBy: CREATED_AT, orderByDirection: DESC }
          ) {
            id
            createdAt
            createdByV2 {
              ...userNameFields
              profileImage
            }
            academicCriteriaSet {
              id
              label
              criteriaType
            }

            academicCriteriaValue {
              id
              label
              abbreviation
              color
            }
          }
        }
      }
    }
  }
  ${userNameFragment}
`;

export const getAssessmentToolsQuery = gql`
  query getAssessmentTools($id: ID!) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        assessmentTools {
          id
          ... on MypObjectiveRubric {
            criteria {
              id
              highestMark
            }
          }
        }
      }
    }
  }
`;

export const getProjectGroupProjectsWithFieldsQuery = gql`
  query getProjectGroupProjectsWithFields(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $projectFieldFilters: PlannerFieldFilter
    $studentCourseFilters: UserCourseMapFilters
    $isServiceAsAction: Boolean!
    $academicYearId: ID
    $skipCourseDetails: Boolean!
    $fetchProjectTasks: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        type
        deadlines @skip(if: $isServiceAsAction) {
          ...deadline
        }
        projects(filters: $projectFilters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ...projectInfoProgress
              tasks @include(if: $fetchProjectTasks) {
                ...projectTaskResponseItem
              }
              fields(filters: $projectFieldFilters) {
                ...fieldsResolvedMinimalTreeFragmentItem
              }
              academicYear @include(if: $isServiceAsAction) {
                id
                startDate
                endDate
              }
              studentCollaborators {
                id
                academicHonesty(
                  filters: { orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC }
                ) {
                  id
                  displaySequence
                  lockingState {
                    state
                  }
                }
                academicHonestyRemarks {
                  id
                  lockingState {
                    state
                  }
                }
                isAcademicHonestyLocked
                student {
                  ...userNameFields
                }
              }
              notificationBadgeCounts {
                unreadPostCount
                unreadPostCommentCount
                unreadCommentCount
                unreadAcademicHonestyCount
                unreadAttachmentCount
                unreadPendingReviewLogCount
                unreadResolvedReviewLogCount
                unreadPendingReviewLogCommentCount
                unreadResolvedReviewLogCommentCount
                unreadAttachmentAnnotatedCount
                unreadProjectEvidenceCommentCount
                unreadProjectTasksCommentsCount
                unreadProjectPresentationCommentsCount
              }
              isCasExternalPortingDone
            }
          }
        }
      }
    }
  }
  ${projectFragments.projectInfoProgressFragment}
  ${projectFieldsResolvedMinimalTreeFragment.fieldsResolvedMinimalTreeFragment}
  ${deadlineFragments.deadlineFragment}
  ${projectTaskResponseFragment}
  ${userNameFragment}
`;
export const getProjectGroupProjectsWithStudentsIdsQuery = gql`
  query getProjectGroupProjectsWithStudentsId(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $projectFieldFilters: PlannerFieldFilter
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
              fields(filters: $projectFieldFilters) {
                value
                pseudoFieldUid
              }
              studentCollaborators {
                id
                student {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const getProjectGroupDeletedProjectsWithFieldsQuery = gql`
  query getProjectGroupDeletedProjectsWithFields(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $projectFieldFilters: PlannerFieldFilter
    $academicYearId: ID
    $isServiceAsAction: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        type
        projects(filters: $projectFilters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ...projectInfoProgress
              fields(filters: $projectFieldFilters) {
                id
                uid
                value
                pseudoFieldUid
              }
              academicYear @include(if: $isServiceAsAction) {
                id
                startDate
                endDate
              }
              studentCollaborators {
                id
                student {
                  ...userNameFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${projectFragments.deletedProjectInfoProgressFragment}
  ${userNameFragment}
`;

export const getProjectGroupProjectsIdsQuery = gql`
  query getProjectGroupProjectsIds(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $shouldFetchPsmIds: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
              studentCollaborators @include(if: $shouldFetchPsmIds) {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getProjectGroupPortfolioWithStudentQuery = gql`
  query getProjectGroupPortfolioWithStudent(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          edges {
            node {
              id
              student {
                ...userItem
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectUserFragment.basicUser}
`;

export const getProjectGroupProjectSupervisorsQuery = gql`
  query getProjectGroupProjectSupervisors(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $academicYearId: ID
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
              supervisors {
                ...supervisorInfo
              }
            }
          }
        }
      }
    }
  }
  ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
`;

export const getProjectGroupProjectDeletedByUsersQuery = gql`
  query getProjectGroupProjectDeletedByUsers(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
              deletedBy {
                ...userNameFields
                type
                ... on Student {
                  alertTypes {
                    ...alertTypeFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${userNameFragment}
`;

export const getProjectGroupProjectStudentsQuery = gql`
  query getProjectGroupProjectStudents(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
              students {
                ...studentInfo
              }
            }
          }
        }
      }
    }
  }
  ${studentFragments.studentInfoFragment}
`;

export const getStudentProjectPortfolioQuery = gql`
  query getStudentProjectPortfolio(
    $id: ID!
    $projectCountFilter: ProjectCountFilter
    $completionStatusFilters: CompletionStatusFilter
    $plannerElementEvidenceFilters: PlannerElementsEvidenceFilter!
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        ...studentProjectPortfolioDetailsItem
        plannerElementEvidences(filters: $plannerElementEvidenceFilters) {
          id
          plannerElementNode {
            id
            metadata
          }
        }
        rejectedProjects: projects(filters: { status: ["REJECTED"] }) {
          totalCount
        }
        notificationBadgeCounts {
          interestsAndGoalsCommentCount
          learningOutcomesCommentCount
          feedbackCount
          feedbackCommentCount
          interviewCount
        }
      }
    }
  }
  ${studentProjectPortfolioFragments.studentProjectPortfolioDetailsFragment}
`;

export const getProjectGroupStudentPortfolioIdQuery = gql`
  query getProjectGroupStudentProjectPortfolio(
    $id: ID!
    $filters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        studentProjectPortfolio(filters: $filters) {
          totalCount
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getUserProjectGroupsQuery = gql`
  query getUserProjectGroups(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserProjectGroupFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        projectGroups(filters: $filters) {
          ...projectGroupDetailsV2
          yearGroup {
            id
            grade {
              id
              globalGrade {
                uid
                id
              }
            }
          }
        }
      }
      ... on Student {
        id
        projectGroups(filters: $filters) {
          ...basicProjectGroupDetails
          yearGroup {
            id
            grade {
              id
              globalGrade {
                uid
                id
              }
            }
          }
        }
      }
    }
  }
  ${projectGroupFragments.basicProjectGroupDetails}
  ${projectGroupFragments.projectGroupDetailsV2}
`;

export const getProjectGroupCategoriesQuery = gql`
  query getProjectGroupCategories(
    $organizationId: ID!
    $filters: ProjectGroupCategoryFilters
  ) {
    node(type: ORGANIZATION, id: $organizationId) {
      ... on Organization {
        id
        projectGroupCategories(filters: $filters) {
          id
          category
          type
          curriculumProgram {
            #This is required for backward compatibility between RnP and Configurations
            id
            type
          }
        }
      }
    }
  }
`;

export const getProjectGroupYearGroupQuery = gql`
  query getProjectGroupYearGroup(
    $id: ID!
    $gradeFilters: YearGroupGradeFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        ...basicProjectGroupDetails
        yearGroup {
          id
          name
          isArchived
          grade(filters: $gradeFilters) {
            id
            name
            displaySequence
          }
        }
      }
    }
  }
  ${projectGroupFragments.basicProjectGroupDetails}
`;

export const getUserProjectGroupYearGroupsQuery = gql`
  query getUserProjectGroupYearGroups(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserProjectGroupFilter
    $gradeFilters: YearGroupGradeFilters
    $projectFilters: ProjectGroupProjectsFilters
    $studentProjectPortfolioFilters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        projectGroups(filters: $filters) {
          ...projectGroupInfoV2
        }
      }

      ... on Student {
        id
        projectGroups(filters: $filters) {
          ...projectGroupInfoV2
        }
      }
    }
  }
  ${projectGroupFragment.projectGroupInfoV2}
`;

export const getProjectGroupAssessmentToolDetailsQuery = gql`
  query getProjectGroupAssessmentToolDetails(
    $id: ID!
    $assessmentToolFilters: AssessmentToolFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        ...projectGroupWithAssessmentToolsItem
      }
    }
  }
  ${projectGroupFragment.projectGroupWithAssessmentTools}
`;

export const getOrganizationAcademicCriteriaValuesQuery = gql`
  query getOrganizationAcademicCriteriaValues(
    $id: ID!
    $finalGradeFilters: RatingLevelsFilter
    $fetchFinalGrade: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          finalGradeRatingLevels: ratingLevels(filters: $finalGradeFilters)
            @include(if: $fetchFinalGrade) {
            ...basicOrganizationElementRatingsItem
          }
        }
      }
    }
  }
  ${projectGroupFragment.basicOrganizationElementRatings}
`;

export const getOrganizationAcademicCriteriaValuesMultiDPQuery = gql`
  query getOrganizationAcademicCriteriaValuesMultiDP(
    $id: ID!
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          academicCriteriaSets(
            filters: {
              criteriaType: CRITERIA_RUBRIC_FINAL_GRADE
              curriculumProgramIds: $curriculumProgramIds
            }
          ) {
            id
            criteriaType
            subType
            academicCriteriaValues {
              id
              label
              description
              abbreviation
              displaySequence
            }
          }
        }
      }
    }
  }
`;

export const getProjectCriteriaDetailsQuery = gql`
  query getProjectCriteriaDetails(
    $id: ID!
    $studentCourseFilters: UserCourseMapFilters
  ) {
    node(id: $id, type: PROJECT) {
      ... on Project {
        id
        students {
          id
          courseMap(filters: $studentCourseFilters) {
            id
            tags {
              id
              type
            }
            subjectWiseLevel {
              level {
                id
                type
              }
            }
          }
        }
        assessmentRemarks {
          ...remarkMessage
        }
        ibPYPElementRatings {
          id
          value
          ibPYPElementId
          ibPYPElementType
          isComputedRating
          academicCriteriaSet {
            id
            criteriaType
          }
          academicCriteriaValue {
            id
            label
            abbreviation
          }
        }
        ibPYPElementRemarks {
          id
          message
          ibPYPElementId
          ibPYPElementType
        }
        toolResponses(userFilter: ALL) {
          ...toolResponseItem
          __typename
        }
      }
    }
  }
  ${remarkFragment.remarkMessageFragment}
  ${toolResponseFragment.toolResponse}
`;

export const getProjectStudentCriteriaDetailsQuery = gql`
  query getProjectStudentCriteriaDetails(
    $id: ID!
    $filtersForGeneric: ibPYPElementRatingFiltersForGeneric
    $elementIds: [ID!]
  ) {
    node(id: $id, type: PROJECT) {
      ... on Project {
        id
        students {
          id
          ibPYPElementRatings(
            dataSet: PROJECT_ASSESSMENT
            filtersForGeneric: $filtersForGeneric
          ) {
            id
            value
            ibPYPElementId
            isComputedRating
            academicCriteriaSet {
              id
              criteriaType
            }
            academicCriteriaValue {
              id
              label
              abbreviation
            }
          }
          ibPypElementRemarks(
            elementTypes: [PROJECT_GROUP]
            elementIds: $elementIds
          ) {
            id
            message
            ibPYPElementId
            ibPYPElementType
          }
        }
        assessmentRemarks {
          ...remarkMessage
        }
      }
    }
  }
  ${remarkFragment.remarkMessageFragment}
`;

export const getProjectGroupDetailsQuery = gql`
  query getProjectGroupDetails(
    $id: ID!
    $assessmentToolFilters: AssessmentToolFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        overview
        ...projectGroupWithAssessmentToolsItem
      }
    }
  }
  ${projectGroupFragment.projectGroupWithAssessmentTools}
`;

export const getProjectGroupSetupGuidanceDetailsQuery = gql`
  query getProjectGroupSetupGuidanceDetails(
    $id: ID!
    $filters: SetupGuidanceFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        setupGuidance(filters: $filters) {
          ...attachmentItem
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const getStudentTagDetailsQuery = gql`
  query getStudentTagDetails(
    $id: ID!
    $studentCourseFilters: UserCourseMapFilters
    $skipCourseDetails: Boolean!
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        ...studentInfo
      }
    }
  }
  ${studentFragments.studentInfoFragmentV2}
`;

export const getProjectGroupCourseQuery = gql`
  query getProjectGroupCourse(
    $id: ID!
    $projectFilters: ProjectFilter
    $courseFilters: CourseFilter
    $projectGroupProjectFilters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        ...projectGroupCoursesItem
      }
    }
  }
  ${projectGroupFragment.projectGroupCourses}
`;

export const getOrganizationCourseAndOtherStaffQuery = gql`
  query getOrganizationCourseAndOtherStaff($id: ID!, $courseIds: [ID!]) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: { courses: $courseIds }) {
          edges {
            node {
              ...userItem
            }
          }
        }
        otherStaff: staff(filters: { excludedCourseIds: $courseIds }) {
          edges {
            node {
              ...userItem
            }
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUser}
`;

export const getGroup4ProjectStudentsDetailsQuery = gql`
  query getGroup4ProjectStudentsDetails(
    $id: ID!
    $projectFilters: ProjectFilter
    $courseFilters: CourseFilter
    $studentFilters: SubjectStudentFilter
    $includeInterdisciplinarySubjects: Boolean!
    $isAccountDepartmentEnabled: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        ...projectGroupSubjectGroupSubjectDetailsItem
      }
    }
  }
  ${projectGroupFragment.projectGroupSubjectGroupSubjectDetails}
`;

export const getGroup4ProjectSubjectDetailsQuery = gql`
  query getGroup4ProjectSubjectsDetails(
    $id: ID!
    $studentFilters: SubjectStudentFilter
    $subjectFilters: SubjectFilterInput
    $isAccountDepartmentEnabled: Boolean!
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        ...projectGroupSubjectDetailsItem
      }
    }
  }
  ${projectGroupFragment.projectGroupSubjectDetails}
`;

export const getProjectGroupSubjectGroupIdQuery = gql`
  query getProjectGroupSubjectGroupId($id: ID!) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        subjectGroup {
          id
        }
      }
    }
  }
`;

export const getYearGroupStudentsQuery = gql`
  query getYearGroupStudents($id: ID!, $filters: GroupStudentFilter) {
    node(id: $id, type: YEAR_GROUP) {
      ... on YearGroup {
        id
        students(filters: $filters) {
          edges {
            node {
              ...userItem
              isArchived
              grades
              alertTypes {
                ...alertTypeFields
              }
              registrationCategory
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
  ${projectUserFragment.basicUser}
`;

export const getProjectGroupCourseAcademicYearsQuery = gql`
  query getProjectGroupCourseAcademicYears($id: ID!) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        courses {
          edges {
            node {
              id
              title
              academicYears {
                id
                startDate
                endDate
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentProjectPortfolioFeedbackQuery = gql`
  query getStudentProjectPortfolioFeedback($id: ID!) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      id
      ... on StudentProjectPortfolio {
        ...studentProjectPortfolioFeedbackItem
      }
    }
  }
  ${studentProjectPortfolioFragments.studentProjectPortfolioFeedbackFragment}
`;

export const getModuleAdminsQuery = gql`
  query getModuleAdmins(
    $id: ID!
    $input: ModuleLvlAccessStaffsInput
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        type
        moduleLvlAccessStaffs(input: $input) {
          moduleName
          staffs {
            ...staffItem
          }
        }
      }
      ... on IbDpCurriculumProgram {
        id
        type
        moduleLvlAccessStaffs(input: $input) {
          moduleName
          staffs {
            ...staffItem
          }
        }
      }
    }
  }
  ${staffFragment.basicStaffDetails}
`;

export const getProjectGroupProjectAndPortfolioCountQuery = gql`
  query getProjectGroupProjectAndPortfolioCount(
    $id: ID!
    $projectFilters: ProjectGroupProjectsFilters
    $portfolioFilters: StudentProjectPortfolioFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        id
        type
        projects(filters: $projectFilters) {
          totalCount
        }
        studentProjectPortfolio(filters: $portfolioFilters) {
          totalCount
        }
      }
    }
  }
`;

export const getOrganizationStaffWithExternalUsersQuery = gql`
  query getOrganizationStaffWithExternalUsers(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
    $externalSupervisorFilters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters) {
          totalCount
          edges {
            node {
              ...supervisorInfo
            }
          }
        }
        externalUsers(filters: $externalSupervisorFilters) {
          totalCount
          edges {
            node {
              ...externalUserItem
            }
          }
        }
      }
    }
  }
  ${supervisorFragments.supervisorInfoFragment}
  ${externalCollaboratorFragment.basicExternalCollaborator}
`;

export const getProjectGroupBasicDetailsQuery = gql`
  query getProjectGroupBasicDetails($projectGroupId: ID!) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      id
      ... on ProjectGroup {
        ...projectGroupBasicDetails
        category
        globalProjectGroupId
      }
    }
  }
  ${projectGroupFragment.projectGroupBasicDetails}
`;

export const getStaffsBasedOnPermissionAndContextsQuery = gql`
  query getStaffsBasedOnPermissionAndContexts(
    $permissionId: ID!
    $contexts: [ACLContextEntityInput!]
    $allowContextBuildInternally: Boolean
    $academicYearId: ID
  ) {
    node(id: $permissionId, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        type
        permission
        enabledUsers(
          context: $contexts
          allowContextBuildInternally: $allowContextBuildInternally
          academicYearId: $academicYearId
        ) {
          ...userItem
        }
      }
    }
  }
  ${projectUserFragment.basicUser}
`;

export const getProjectApprovedSupervisorsQuery = gql`
  query getProjectApprovedSupervisors(
    $projectGroupId: ID!
    $filters: ProjectGroupSupervisorFilters
  ) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        supervisors(filters: $filters) {
          edges {
            node {
              id
              staff {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrgAdminsQuery = gql`
  query getOrganizationStaff(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters) {
          totalCount
          edges {
            node {
              ...userNameFields
              isArchived
              type
            }
          }
        }
      }
    }
  }
  ${userNameFragment}
`;

export const getDPSubjectGroupsQuery = gql`
  query getDPSubjectGroups(
    $id: ID!
    $filters: SubjectGroupFilterInput
    $subjectFilters: SubjectFilterInput
    $flatSubjectVariantFilters: FlatSubjectVariantFilterInput
    $plannerNodeFilters: AssociatedChildPlannerElementNodesFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        subjectGroups(filters: $filters) {
          id
          name
          subjects(filters: $subjectFilters) {
            id
            name
            metadata
            associatedChildPlannerElementNodes(filters: $plannerNodeFilters) {
              id
              label
              type
              isLeaf
              children
              subText
              metadata
            }
            flatVariants(filters: $flatSubjectVariantFilters) {
              responseLanguage {
                id
                label
              }
              isArchived
            }
            isArchived
          }
        }
      }
    }
  }
`;

export const getFinalGradeRatingQuery = gql`
  query getFinalGradeRating($id: ID!, $filters: PointAssessmentSetFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        pointAssessmentSets(filters: $filters) {
          edges {
            node {
              id
              type
              categories {
                id
                label
                metadata
                displaySequence
                description
              }
            }
          }
        }
      }
    }
  }
`;

export const getLearningOutcomesPostsQuery = gql`
  query getLearningOutcomesPosts(
    $id: ID!
    $filters: fieldPlannerElementAssociatedNodesInput!
  ) {
    node(id: $id, type: PROJECT) {
      id
      ... on Project {
        fieldPlannerElementAssociatedNodes(filters: $filters) {
          parentId
          associatedProjectEvidences {
            id
          }
        }
      }
    }
  }
`;

export const getStudentPortfolioProjectIdsQuery = gql`
  query getStudentProjectPortfolioProjectsCircularSwitcher(
    $id: ID!
    $projectFilters: ProjectFilter
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        projects(filters: $projectFilters) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getStudentRatingDetailQuery = gql`
  query getStudentRatingDetail(
    $id: ID!
    $filtersForGeneric: ibPYPElementRatingFiltersForGeneric
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        ...userNameFields
        profileImage
        registrationCategory
        ibPYPElementRatings(
          dataSet: PROJECT_ASSESSMENT
          filtersForGeneric: $filtersForGeneric
        ) {
          id
          value
          ibPYPElementId
          isComputedRating
          academicCriteriaSet {
            id
            criteriaType
          }
          academicCriteriaValue {
            id
            label
            abbreviation
          }
          parent {
            id
            ... on Project {
              projectGroup {
                id
                type
              }
            }
          }
        }
      }
    }
  }
  ${userNameFragment}
`;

export const getStudentProjectPortfolioHeaderDetailsQuery = gql`
  query getStudentProjectPortfolioHeaderDetails(
    $id: ID!
    $academicYearId: ID
    $projectCountFilter: ProjectCountFilter
    $ibPYPElementRatingsFilters: ibPYPElementRatingsFilter
    $completionStatusFilters: CompletionStatusFilter
  ) {
    node(id: $id, type: STUDENT_PROJECT_PORTFOLIO) {
      ... on StudentProjectPortfolio {
        id
        projectCounts {
          projectsCount(filters: $projectCountFilter)
          experiencesCount
        }
        status
        completionStatus(filters: $completionStatusFilters) {
          isComplete
          markedCompletedAt
          markedCompletedBy {
            ...userNameFields
          }
        }
        student {
          ...userItem
        }
        staff {
          ...userItem
        }
        projects {
          edges {
            node {
              id
              status
              isArchived
            }
          }
        }
        ibPYPElementRatings(filters: $ibPYPElementRatingsFilters) {
          id
          createdAt
          createdByV2 {
            ...userNameFields
            profileImage
          }
          academicCriteriaSet {
            id
            criteriaType
          }
          academicCriteriaValue {
            id
            label
            abbreviation
            color
          }
        }
      }
    }
  }
  ${projectUserFragment.basicUserWithIsArchivedV2}
  ${userNameFragment}
`;

export const getOrganizationCriteriaSetQuery = gql`
  query getOrganizationConstant(
    $id: ID!
    $filters: academicCriteriaSetsFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          academicCriteriaSets(filters: $filters) {
            id
            label
            criteriaType
            academicCriteriaValues {
              id
              label
              color
              abbreviation
            }
          }
        }
      }
    }
  }
`;

export const getProjectGroupSettingsQuery = gql`
  query getProjectGroupSettings(
    $id: ID!
    $settingsFilter: ProjectGroupSettingsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        settings(filters: $settingsFilter) {
          id
          setting
          value
        }
      }
    }
  }
`;

export const getProjectGroupAcademicYearsQuery = gql`
  query getProjectGroupAcademicYears(
    $id: ID!
    $filters: AcademicYearGradeMapFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        yearGroup {
          id
          academicYearGradeMap(filters: $filters) {
            academicYear {
              id
              startDate
              endDate
              isCurrentAcademicYear
            }
          }
        }
      }
    }
  }
`;

export const getProjectGroupDeadlinesWithProjectQuery = gql`
  query getProjectGroupDeadlinesV2($projectGroupId: ID!) {
    node(id: $projectGroupId, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        tasks {
          edges {
            node {
              id

              title
              description
              dueOn
              closesOn
              isStudentSubmissionEnabled
              isDeadlineAllDay

              responses {
                id
                isComplete
                latestSubmissionResponse {
                  id

                  status
                }

                project {
                  id
                  students {
                    id
                    firstName
                    middleName
                    lastName
                    profileImage
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationTemplates = gql`
  query getOrganizationTemplates(
    $id: ID!
    $filters: OrganizationTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        templates(filters: $filters) {
          id
          body
        }
      }
    }
  }
`;
